/**
 * ExpensesTable - DataTable for expenses with filters and row actions
 */

import { PlusIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { DataTable } from '@/components/common/DataTable';
import { SkeletonTable } from '@/components/common/DataTable/SkeletonTable';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLedgerToast } from '@/hooks/useLedgerToast';
import {
  ReadExpense,
  useExpenseDestroyMutation,
  useExpenseListQuery,
} from '@/services/api/ledgerService';

import { DeleteConfirmDialog } from '../shared/DeleteConfirmDialog';
import { parseDateOnly } from '../shared/formatUtils';
import { LedgerEmptyState } from '../shared/LedgerEmptyState';
import { createExpenseColumns } from './expensesColumns';

interface ExpensesTableProps {
  matterId: string;
  /** Optional provider ID to pre-filter expenses by provider */
  providerId?: string;
  /** Callback when user wants to add a new expense */
  onAddExpense?: () => void;
  /** Callback when user wants to edit an expense */
  onEditExpense: (expense: ReadExpense) => void;
  /** Callback when user wants to edit allocations for an expense */
  onEditAllocations: (expense: ReadExpense) => void;
}

export const ExpensesTable = ({
  matterId,
  providerId,
  onAddExpense,
  onEditExpense,
  onEditAllocations,
}: ExpensesTableProps) => {
  const { showSuccess, showError } = useLedgerToast();

  // Filter state
  const [providerFilter, setProviderFilter] = useState<string | undefined>(
    providerId,
  );
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Sort state
  const [sortField, setSortField] = useState<string>('service_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Delete state
  const [expenseToDelete, setExpenseToDelete] = useState<ReadExpense | null>(
    null,
  );

  // API hooks - fetch ALL expenses (don't filter on API level)
  const {
    data: allExpenses = [],
    isLoading,
    isFetching,
  } = useExpenseListQuery({
    matterId,
  });

  const [deleteExpense, { isLoading: isDeleting }] =
    useExpenseDestroyMutation();

  // Apply client-side filtering and sorting
  const filteredExpenses = useMemo(() => {
    let filtered = allExpenses;

    // Filter by provider
    if (providerFilter) {
      filtered = filtered.filter((e) => e.provider_id === providerFilter);
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter((expense) => {
        const totalAllocated = expense.allocations.reduce(
          (sum, alloc) => sum + parseFloat(alloc.amount),
          0,
        );
        const totalBilled = parseFloat(expense.billed_amount);
        const hasUnallocated = expense.allocations.some(
          (a) => a.allocation_bucket === 'UNALLOCATED',
        );

        if (statusFilter === 'unallocated') {
          return (
            expense.allocations.length === 0 ||
            totalAllocated === 0 ||
            (hasUnallocated && expense.allocations.length === 1)
          );
        }
        if (statusFilter === 'partial') {
          return (
            hasUnallocated || Math.abs(totalBilled - totalAllocated) > 0.01
          );
        }
        if (statusFilter === 'allocated') {
          return (
            !hasUnallocated && Math.abs(totalBilled - totalAllocated) < 0.01
          );
        }
        return true;
      });
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';

      if (sortField === 'service_date') {
        aVal = parseDateOnly(a.service_date).getTime();
        bVal = parseDateOnly(b.service_date).getTime();
      } else if (sortField === 'provider') {
        aVal = a.provider?.name ?? '';
        bVal = b.provider?.name ?? '';
      } else if (sortField === 'billed_amount') {
        aVal = parseFloat(a.billed_amount);
        bVal = parseFloat(b.billed_amount);
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [allExpenses, providerFilter, statusFilter, sortField, sortDirection]);

  // Handle sort change
  const handleSortChange = useCallback((columnId: string) => {
    setSortField((prev) => {
      if (prev === columnId) {
        // Toggle direction
        setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      // New column, default to desc for dates, asc for others
      setSortDirection(columnId === 'service_date' ? 'desc' : 'asc');
      return columnId;
    });
  }, []);

  // Column actions
  const columnActions = useMemo(
    () => ({
      onEdit: onEditExpense,
      onEditAllocations,
      onDelete: (expense: ReadExpense) => setExpenseToDelete(expense),
    }),
    [onEditExpense, onEditAllocations],
  );

  const columns = useMemo(
    () => createExpenseColumns(columnActions),
    [columnActions],
  );

  // Delete handlers
  const handleDeleteConfirm = useCallback(async () => {
    if (!expenseToDelete) return;

    try {
      await deleteExpense({
        matterId,
        id: expenseToDelete.id,
      }).unwrap();
      showSuccess('delete', 'expense');
      setExpenseToDelete(null);
    } catch {
      showError();
    }
  }, [expenseToDelete, deleteExpense, matterId, showSuccess, showError]);

  const handleDeleteCancel = useCallback(() => {
    setExpenseToDelete(null);
  }, []);

  // Get unique providers from ALL expenses for the filter (not filtered)
  const providerOptions = useMemo(() => {
    const uniqueProviders = new Map<string, string>();
    allExpenses.forEach((expense) => {
      if (expense.provider_id && expense.provider?.name) {
        uniqueProviders.set(expense.provider_id, expense.provider.name);
      }
    });
    return Array.from(uniqueProviders.entries()).map(([id, name]) => ({
      id,
      name,
    }));
  }, [allExpenses]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-9 w-48 bg-gray-100 animate-pulse rounded-md" />
            <div className="h-9 w-32 bg-gray-100 animate-pulse rounded-md" />
            <div className="h-6 w-32 bg-gray-100 animate-pulse rounded-md" />
          </div>
          <div className="h-9 w-32 bg-gray-100 animate-pulse rounded-md" />
        </div>
        <SkeletonTable columns={columns} rowCount={5} density="compact" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unallocated">Unallocated</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="allocated">Allocated</SelectItem>
            </SelectContent>
          </Select>

          {/* Provider Filter */}
          <Select
            value={providerFilter ?? 'all'}
            onValueChange={(value) =>
              setProviderFilter(value === 'all' ? undefined : value)
            }
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All providers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All providers</SelectItem>
              {providerOptions.map((provider) => (
                <SelectItem key={provider.id} value={provider.id}>
                  {provider.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Add Expense Button */}
        {onAddExpense && (
          <Button
            onClick={onAddExpense}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            Add Expense
          </Button>
        )}
      </div>

      {/* Table */}
      {filteredExpenses.length === 0 && !isFetching ? (
        <LedgerEmptyState
          title="No expenses found"
          description={
            statusFilter !== 'all' || providerFilter
              ? 'Try adjusting your filters'
              : 'Add expenses to track medical costs and allocations.'
          }
          action={
            !statusFilter && !providerFilter && onAddExpense
              ? {
                  label: 'Add expense',
                  onClick: onAddExpense,
                }
              : undefined
          }
        />
      ) : (
        <DataTable
          data={filteredExpenses}
          columns={columns}
          isLoading={isFetching}
          getRowKey={(row) => row.id}
          density="compact"
          emptyMessage="No expenses found"
          onRowClick={onEditExpense}
          onSortChange={handleSortChange}
          sortField={sortField}
          sortDirection={sortDirection}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        entityType="expense"
        open={!!expenseToDelete}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDeleting={isDeleting}
      />
    </div>
  );
};
